// Define the SlideNumber extension
function SlideNumber(Splide, Components) {
  const { root } = Components.Elements;
  let elm;

  function mount() {
    elm = document.createElement('div');
    elm.className = 'splide__slide-number';
    elm.style.position = 'absolute';
    elm.style.bottom = '10px';
    elm.style.left = '10px';
    elm.style.backgroundColor = 'rgba(0,0,0,0.5)';
    elm.style.color = 'white';
    elm.style.padding = '5px 10px';
    elm.style.borderRadius = '5px';
    elm.style.fontSize = '14px';
    root.appendChild(elm);
    update();
    Splide.on('moved', update);
  }

  function update() {
    elm.textContent = `${Splide.index + 1}/${Splide.length}`;
  }

  return {
    mount,
    destroy() {
      if (elm && elm.parentNode) {
        elm.parentNode.removeChild(elm);
      }
    },
  };
}

// Sets up custom play/pause buttons for Wistia <wistia-player> components (Aurora)
// Accepts the specific 'section' element to scope the query
function setupWistiaControls(section) {
  const wistiaControlButtons = section.querySelectorAll(
    '.wistia__content .play-pause__toggle, .custom-video-container .play-pause__toggle'
  );

  wistiaControlButtons.forEach((button) => {
    // Avoid double-binding if initializeSliders runs more than once
    if (button.dataset.wistiaBound === '1') return;
    button.dataset.wistiaBound = '1';

    const contentWrapper = button.closest('.wistia__content, .custom-video-container');
    if (!contentWrapper) return;

    // Handle both Wistia players and native HTML5 videos
    const player = contentWrapper.querySelector('wistia-player, video');
    if (!player) return;

    const accessibilityText = button.querySelector('.visually-hidden');

    const syncButtonState = () => {
      const pausedOrEnded = player.paused || player.state === 'ended';
      button.classList.toggle('is-paused', pausedOrEnded);

      const label = pausedOrEnded ? 'Play video' : 'Pause video';
      if (accessibilityText) accessibilityText.textContent = label;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-pressed', (!pausedOrEnded).toString());
    };

    // Toggle on click
    button.addEventListener('click', (e) => {
      e.preventDefault();
      if (player.paused || player.state === 'ended') {
        player.play();   
      } else {
        player.pause();  
      }
    });

    const bind = () => {
      player.removeEventListener('play', syncButtonState);
      player.removeEventListener('pause', syncButtonState);
      player.removeEventListener('ended', syncButtonState);

      player.addEventListener('play', syncButtonState);
      player.addEventListener('pause', syncButtonState);
      player.addEventListener('ended', syncButtonState);

      syncButtonState();
    };

    if (player.tagName.toLowerCase() === 'video' || typeof player.state !== 'undefined') {
      bind();
    } else {
      player.addEventListener('api-ready', bind);
    }
  });
}

// Function to initialize sliders
function initializeSliders() {
  // 1. Find ALL product sections on the page
  const productSections = document.querySelectorAll('.product[data-section-type="product"]');

  // 2. Loop through each section individually
  productSections.forEach((section) => {
    
    // Prevent double-initialization
    if (section.dataset.slidersInitialized === 'true') return;
    section.dataset.slidersInitialized = 'true';

    // 3. Find elements strictly WITHIN this specific section
    const mainSliderElement = section.querySelector('.product__slideList');
    const thumbnailSliderElement = section.querySelector('.product__thumbList');

    // Exit if sliders don't exist in this specific section
    if (!mainSliderElement || !thumbnailSliderElement) {
      return;
    }

    var main = new Splide(mainSliderElement, {
      type: 'slide',
      rewind: false,
      pagination: false,
      arrows: false,
      breakpoints: {
        1024: {},
        768: {
          padding: { right: '5rem' },
        },
      },
    });

    var thumbnails = new Splide(thumbnailSliderElement, {
      fixedWidth: 191,
      fixedHeight: 254,
      height: 746,
      gap: 10,
      rewind: false,
      pagination: false,
      cover: true,
      isNavigation: true,
      direction: 'ttb',
      breakpoints: {
        1024: {
          direction: 'ltr',
        },
        768: {
          destroy: true,
        },
      },
    });

    // Function to pause all Wistia videos in this section
    function pauseAllWistiaVideos() {
      var wistiaEmbeds = section.querySelectorAll('.wistia_embed_new');
      wistiaEmbeds.forEach(function (wistiaEmbed) {
        if (wistiaEmbed && typeof wistiaEmbed.pause === 'function') {
          wistiaEmbed.pause();
        }
      });
    }

    // Function to play Wistia video in active slide in THIS section
    function playActiveSlideVideo() {
      var activeIndex = main.index;
      var slides = main.Components.Elements.slides;
      var activeSlide = slides[activeIndex];
      var wistiaEmbed = activeSlide.querySelector('.wistia_embed_new');

      if (wistiaEmbed && typeof wistiaEmbed.play === 'function') {
        wistiaEmbed.play();
      }
    }

    main.on('move', pauseAllWistiaVideos);
    main.on('moved', playActiveSlideVideo);
    main.sync(thumbnails);

    main.mount();
    thumbnails.mount();

    var slideNumberExtension = new SlideNumber(main, main.Components);
    slideNumberExtension.mount();

    main.on('destroy', function () {
      slideNumberExtension.destroy();
    });

    // 4. Handle variant changes scoped to THIS section
    function setupVariantImageSwitching() {
      // 1. Get the section ID to uniquely identify the JSON block
      const sectionId = section.getAttribute('data-section-id');
      
      // 2. Query the document (not the section) because the JSON block is outside the <section> tag
      const productJsonElement = document.getElementById('ProductJson-' + sectionId);
      
      // 3. Look for the form inside the section, fallback to document just in case
      const productForm = section.querySelector('form[action^="/cart/add"]') || document.querySelector('form[action^="/cart/add"]');
      
      // Bail out if either is missing
      if (!productJsonElement || !productForm) {
        console.warn('Splide Variant Switching: Could not find JSON or Form for section ' + sectionId);
        return;
      }

      const productJson = JSON.parse(productJsonElement.textContent);

      const updateSliderForVariant = (variantId) => {
        if (!variantId) return;

        const selectedVariant = productJson.variants.find(variant => variant.id == variantId);

        // Fallback checks for both newer and older Shopify image property names
        const media = selectedVariant?.featured_media || selectedVariant?.featured_image;

        if (media && media.id) {
          const featuredMediaId = media.id;
          
          // Use your proven old-script logic to find the specific slide node
          const targetSlide = mainSliderElement.querySelector(`.splide__slide[data-option="${featuredMediaId}"]`);

          if (targetSlide) {
            const slides = Array.from(mainSliderElement.querySelectorAll('.splide__slide'));
            const slideIndex = slides.indexOf(targetSlide);

            if (slideIndex > -1) {
              main.go(slideIndex);
            }
          }
        }
      };

      // Listen for variant changes
      productForm.addEventListener('change', () => {
        // Slight delay ensures we read the FormData after the theme updates the hidden input
        setTimeout(() => {
          const selectedVariantId = new FormData(productForm).get('id');
          updateSliderForVariant(selectedVariantId);
        }, 50);
      });

      // Set the initial slide on page load
      const initialVariantId = new FormData(productForm).get('id');
      updateSliderForVariant(initialVariantId);
    }

    setupVariantImageSwitching();
    
    // Call Wistia setup passing the section container
    setupWistiaControls(section);
  });
}

// Add event listeners
document.addEventListener('DOMContentLoaded', initializeSliders);
document.addEventListener('shopify:section:load', initializeSliders);